import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, Select, Modal, Table, StatusPill, EmptyState, useToast } from '../../design-system';
import '../styles/Documents.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

// A document's OWN date arrives as a bare 'YYYY-MM-DD' — a day, not an instant.
// Formatted in UTC on purpose: `new Date('2026-08-01')` is parsed as UTC
// midnight and would render as 31 Jul for anyone west of Greenwich. See
// src/utils/documentDate.js for the same reasoning at the other end.
const fmtDocDate = (s) => {
  if (!s) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' });
};
const fmtBytes = (n) => {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};
const statusTone = { draft: 'neutral', final: 'info', filed: 'success', archived: 'neutral' };

const EMPTY = { title: '', category: 'OTHER', financialYear: '', documentDate: '', tags: '', status: 'final', clientId: '' };

// OneDrive backup-mirror status. 'done' = safely on both S3 and OneDrive;
// 'pending' = on S3, retry job is mirroring to OneDrive; 'failed' = give up
// after retries (still safe on S3); 'disabled' = OneDrive not configured; links
// have no bytes to back up.
const backupPill = (b) => {
  if (!b) return <span className="doc-muted">—</span>;
  const map = {
    done: { variant: 'success', label: '☁ Backed up' },
    pending: { variant: 'warning', label: 'Backing up…' },
    failed: { variant: 'danger', label: 'Backup failed' },
    disabled: { variant: 'neutral', label: 'S3 only' },
  };
  const cfg = map[b.status] || map.disabled;
  const pill = <StatusPill variant={cfg.variant} label={cfg.label} />;
  // A 'done' mirror links straight to the copy in OneDrive.
  return b.status === 'done' && b.webUrl ? (
    <a href={b.webUrl} target="_blank" rel="noreferrer" title="Open in OneDrive" style={{ textDecoration: 'none' }}>{pill}</a>
  ) : (
    <span title={b.lastError || ''}>{pill}</span>
  );
};

// Firm document library / vault. Modes, driven by props:
//   • clientId set            → the per-client vault (client fixed, no client column).
//   • suspenseMode            → the "Suspense" inbox: documents with no client yet,
//                               uploaded without knowing where they belong. Move
//                               is the primary action — refile into a client/folder.
//   • neither (allView)       → the flat org-wide library (pick client on upload).
// Extra props for the folder browser: `group` narrows to one category group,
// `month` narrows further to one month bucket ('YYYY-MM', or '__undated__' for
// documents carrying no date), `embedded` strips the outer chrome/filters (the
// browser supplies breadcrumbs), and `onChanged` fires after any mutation so the
// browser can refresh its counts.
function DocumentsManager({ clientId = null, group = null, month = null, suspenseMode = false, embedded = false, onChanged = null }) {
  const allView = !clientId && !suspenseMode;
  const toast = useToast();
  const fileRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [clients, setClients] = useState([]);
  const [cats, setCats] = useState({ groups: [], categories: [] });
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({ client: '', group: '', financialYear: '', q: '' });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { doc, url, kind, loading } | null
  const [customName, setCustomName] = useState(false); // upload: custom vs system name
  const [namePreview, setNamePreview] = useState(''); // live system-generated name
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [editDoc, setEditDoc] = useState(null);
  const [moveDoc, setMoveDoc] = useState(null); // { id, title, clientId, category, financialYear } | null
  const [moving, setMoving] = useState(false);

  const notifyChanged = useCallback(() => { if (onChanged) onChanged(); }, [onChanged]);

  // First category code of a group — the sensible default when uploading into a
  // group folder.
  const firstCatOfGroup = useCallback(
    (gid) => cats.categories.find((c) => c.group === gid)?.code || 'OTHER',
    [cats]
  );

  // Static lookups. Categories always; clients whenever we can move/upload across
  // clients (all-view, suspense, and the Move modal — so fetch them everywhere).
  useEffect(() => {
    apiClient.get('/api/documents/categories').then((r) => setCats(r.data)).catch(() => {});
    apiClient.get('/api/clients/options').then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const catName = useCallback((code) => cats.categories.find((c) => c.code === code)?.name || code, [cats]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (suspenseMode) params.clientId = 'firm'; // firm/null bucket = Suspense
    else if (clientId) params.clientId = clientId;
    else if (filters.client) params.clientId = filters.client;
    if (group) params.group = group;
    else if (filters.group) params.group = filters.group;
    if (filters.financialYear) params.financialYear = filters.financialYear;
    if (month) params.month = month;
    if (filters.q) params.q = filters.q;
    apiClient
      .get('/api/documents', { params })
      .then((r) => setDocs(r.data))
      .catch(() => toast.danger('Failed to load documents'))
      .finally(() => setLoading(false));
    // ⚠️ `toast` IS DELIBERATELY NOT A DEPENDENCY — `load` is called from an
    // effect, so anything that changes its identity re-fetches. Keying it on
    // the notification its own failure raises is a retry loop waiting for an
    // outage. See the note in design-system/Toast.jsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, suspenseMode, group, month, filters]);

  useEffect(() => {
    load();
  }, [load]);

  const openUpload = () => {
    setForm({
      ...EMPTY,
      // Uploading INSIDE a month folder prefills that month's 1st, so the file
      // lands in the folder you are standing in instead of silently falling out
      // of it into "Undated". Still editable to the real day. The undated bucket
      // deliberately prefills nothing.
      documentDate: /^\d{4}-\d{2}$/.test(month || '') ? `${month}-01` : '',
      // In a group folder, default the category to that group's first type.
      category: group ? firstCatOfGroup(group) : 'OTHER',
      // Suspense uploads deliberately have no client; per-client fixes it.
      clientId: suspenseMode ? '' : clientId || filters.client || '',
    });
    setFile(null);
    setFormError('');
    setCustomName(false);
    setNamePreview('');
    setUploadOpen(true);
  };

  // Live system-name preview: while the modal is open and NOT in custom mode,
  // ask the backend what it would auto-name the doc (with the client's real
  // PAN/GSTIN folded in). Debounced so typing the FY doesn't spam requests.
  useEffect(() => {
    if (!uploadOpen || customName) return undefined;
    const params = { category: form.category };
    const cid = suspenseMode ? form.clientId : clientId || form.clientId;
    if (cid) params.clientId = cid;
    if (form.financialYear.trim()) params.financialYear = form.financialYear.trim();
    let cancelled = false;
    const t = setTimeout(() => {
      apiClient
        .get('/api/documents/name-preview', { params })
        .then((r) => !cancelled && setNamePreview(r.data.name))
        .catch(() => !cancelled && setNamePreview(''));
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [uploadOpen, customName, form.category, form.clientId, form.financialYear, clientId, suspenseMode]);

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!file) return setFormError('Choose a file');
    const target = suspenseMode ? form.clientId : clientId || form.clientId;
    setSaving(true);
    setFormError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (target) fd.append('clientId', target);
      fd.append('category', form.category);
      // Only send a title when the user opted for a custom name; otherwise the
      // backend applies the system naming convention.
      if (customName && form.title.trim()) fd.append('title', form.title.trim());
      if (form.financialYear.trim()) fd.append('financialYear', form.financialYear.trim());
      if (form.documentDate) fd.append('documentDate', form.documentDate);
      if (form.tags.trim()) fd.append('tags', form.tags.trim());
      fd.append('status', form.status);
      await apiClient.post('/api/documents', fd);
      setUploadOpen(false);
      toast.success(target ? 'Document uploaded' : 'Uploaded to Suspense — file it to a client when ready');
      load();
      notifyChanged();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  // Download goes through apiClient so the auth token is sent + the access is
  // logged server-side; the blob is then saved locally.
  // Save-as name: the convention title + the original extension (so the file on
  // disk reads "Sharma Textiles - PAN Card - … .png", not the raw upload name).
  const saveAsName = (doc) => {
    const dot = (doc.fileName || '').lastIndexOf('.');
    const ext = dot > -1 ? doc.fileName.slice(dot) : '';
    let base = (doc.title || doc.fileName || 'document').trim();
    if (ext && base.toLowerCase().endsWith(ext.toLowerCase())) base = base.slice(0, -ext.length);
    base = base.replace(/[/\\:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'document';
    return `${base}${ext}`;
  };

  const download = async (doc) => {
    try {
      const res = await apiClient.get(`/api/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = saveAsName(doc);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.danger('Download failed');
    }
  };

  // What we can render inline. Everything else falls back to a download prompt.
  const previewKind = (doc) => {
    const m = (doc.mimeType || '').toLowerCase();
    if (m.startsWith('image/')) return 'image';
    if (m === 'application/pdf') return 'pdf';
    if (m.startsWith('text/') || m === 'application/json') return 'text';
    return 'other';
  };

  // Open an in-app preview. Asks the backend for a short-lived SIGNED S3 URL and
  // renders that directly — so the image/PDF loads straight from S3, not proxied
  // as a blob through the backend. Links open their own URL; local-stored docs
  // get a streaming URL fallback (prefixed with the API base).
  const openPreview = async (doc) => {
    if (doc.source === 'link') {
      window.open(doc.fileUrl, '_blank', 'noopener');
      return;
    }
    setPreview({ doc, url: null, kind: previewKind(doc), loading: true });
    try {
      const res = await apiClient.get(`/api/documents/${doc.id}/url`, { params: { inline: 'true' } });
      let url = res.data.url;
      // 'stream' fallback is a relative API path — needs the token, so fetch it
      // as a blob the old way. 's3'/'link' are absolute URLs used directly.
      if (res.data.source === 'stream') {
        const blobRes = await apiClient.get(`/api/documents/${doc.id}/download`, { responseType: 'blob', params: { inline: 'true' } });
        url = URL.createObjectURL(new Blob([blobRes.data], { type: doc.mimeType || 'application/octet-stream' }));
      }
      setPreview((p) => (p && p.doc.id === doc.id ? { ...p, url, loading: false } : p));
    } catch {
      toast.danger('Preview failed');
      setPreview(null);
    }
  };

  const closePreview = () => {
    setPreview((p) => {
      // Only blob: URLs need revoking; signed S3 URLs are plain strings.
      if (p?.url && p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
      return null;
    });
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title || doc.fileName}"?`)) return;
    try {
      await apiClient.delete(`/api/documents/${doc.id}`);
      toast.success('Document deleted');
      load();
      notifyChanged();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Delete failed');
    }
  };

  const saveEdit = async () => {
    try {
      await apiClient.put(`/api/documents/${editDoc.id}`, {
        title: editDoc.title,
        category: editDoc.category,
        financialYear: editDoc.financialYear || '',
        // '' clears the date — the server reads it as an explicit null, which is
        // how a date typed onto the wrong document gets taken back off.
        documentDate: editDoc.documentDate || '',
        status: editDoc.status,
        tags: (editDoc.tags || []).join(', '),
      });
      setEditDoc(null);
      toast.success('Document updated');
      load();
      notifyChanged();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Update failed');
    }
  };

  // Move / refile — assign (or reassign) a document's client AND folder in one
  // step. This is how a Suspense document lands inside a client's vault.
  const openMove = (doc) => {
    setMoveDoc({
      id: doc.id,
      title: doc.title || doc.fileName,
      clientId: doc.clientId || '',
      category: doc.category || 'OTHER',
      financialYear: doc.financialYear || '',
    });
  };

  const submitMove = async () => {
    setMoving(true);
    try {
      await apiClient.put(`/api/documents/${moveDoc.id}`, {
        clientId: moveDoc.clientId || '', // '' → null (back to Suspense)
        category: moveDoc.category,
        financialYear: moveDoc.financialYear || '',
      });
      setMoveDoc(null);
      toast.success(moveDoc.clientId ? 'Document filed' : 'Document moved to Suspense');
      load();
      notifyChanged();
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  const groupedOptions = cats.groups.map((g) => (
    <optgroup key={g.id} label={g.name}>
      {cats.categories.filter((c) => c.group === g.id).map((c) => (
        <option key={c.code} value={c.code}>{c.name}</option>
      ))}
    </optgroup>
  ));

  const showClientCol = allView;

  const columns = [
    {
      key: 'title',
      header: 'Document',
      render: (r) => (
        <div className="doc-name">
          <span className="doc-file-ico" aria-hidden>{r.source === 'link' ? '🔗' : '📄'}</span>
          <div>
            <button type="button" className="doc-link" onClick={() => openPreview(r)} title="Preview">{r.title || r.fileName}</button>
            <span className="doc-sub">{catName(r.category)}{r.financialYear ? ` · FY ${r.financialYear}` : ''}</span>
          </div>
        </div>
      ),
    },
    ...(showClientCol ? [{ key: 'client', header: 'Client', render: (r) => r.client?.name || <span className="doc-muted">Suspense</span> }] : []),
    { key: 'status', header: 'Status', render: (r) => <StatusPill variant={statusTone[r.status] || 'neutral'} label={r.status} /> },
    { key: 'backup', header: 'Backup', render: (r) => backupPill(r.backup) },
    { key: 'sizeBytes', header: 'Size', render: (r) => fmtBytes(r.sizeBytes) },
    // The document's own date — what the month folders are built from. Distinct
    // from "Uploaded" beside it, which is when the office scanned it.
    {
      key: 'documentDate',
      header: 'Date',
      render: (r) =>
        r.documentDate ? (
          <span title="Date on the document">{fmtDocDate(r.documentDate)}</span>
        ) : (
          <span className="doc-muted" title="No date set — filed under Undated">—</span>
        ),
    },
    { key: 'uploaded', header: 'Uploaded', render: (r) => <span title={r.uploadedByName || ''}>{fmtDate(r.uploadedAt)}</span> },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="doc-actions">
          {/* Move is the headline action in Suspense (file it away); elsewhere it's
              a quieter "refile" option. */}
          <Button type="button" variant={suspenseMode ? 'primary' : 'ghost'} size="sm" onClick={() => openMove(r)}>
            {suspenseMode ? 'File it' : 'Move'}
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => openPreview(r)}>Preview</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => download(r)}>Download</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditDoc({ ...r, financialYear: r.financialYear || '' })}>Edit</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => remove(r)}>Delete</Button>
        </div>
      ),
    },
  ];

  const emptyState = suspenseMode ? (
    <EmptyState icon="✅" title="Suspense is empty" description="Nothing waiting to be filed — every uploaded document is assigned to a client." />
  ) : (
    <EmptyState icon="📁" title="No documents" description="Upload PAN cards, GST certificates, financials, DSCs and more." action={<Button type="button" variant="primary" size="sm" onClick={openUpload}>+ Upload</Button>} />
  );

  const body = (
    <>
      {/* Filters — hidden when embedded in the folder browser (folders navigate). */}
      {!embedded && (
        <div className="doc-filters">
          {allView && (
            <Select aria-label="Client" value={filters.client} onChange={(e) => setFilters({ ...filters, client: e.target.value })}>
              <option value="">All clients</option>
              <option value="firm">Suspense (unfiled)</option>
              {clients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
            </Select>
          )}
          {!group && (
            <Select aria-label="Category group" value={filters.group} onChange={(e) => setFilters({ ...filters, group: e.target.value })}>
              <option value="">All categories</option>
              {cats.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
          )}
          <Input placeholder="FY e.g. 2025-26" value={filters.financialYear} onChange={(e) => setFilters({ ...filters, financialYear: e.target.value })} />
          <Input placeholder="Search name…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </div>
      )}
      {/* A slim search bar when embedded, so the leaf is still searchable. */}
      {embedded && (
        <div className="doc-filters doc-filters-slim">
          <Input placeholder="Search this folder…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </div>
      )}

      {loading ? (
        <p className="doc-hint">Loading…</p>
      ) : docs.length === 0 ? (
        emptyState
      ) : (
        <Table keyField="id" columns={columns} data={docs} />
      )}
    </>
  );

  const uploadBtn = <Button type="button" variant="primary" size="sm" onClick={openUpload}>+ Upload</Button>;

  return (
    <>
      {embedded ? (
        <div className="doc-leaf">
          <div className="doc-leaf-toolbar">{uploadBtn}</div>
          {body}
        </div>
      ) : (
        <Card>
          <Card.Header
            title="Documents"
            subtitle={
              suspenseMode
                ? 'Unfiled uploads — assign each to a client and folder'
                : allView
                ? "Your firm's document library across all clients"
                : "This client's documents"
            }
            action={uploadBtn}
          />
          {body}
        </Card>
      )}

      {/* Upload */}
      {uploadOpen && (
        <Modal
          isOpen
          onClose={() => setUploadOpen(false)}
          title="Upload document"
          footer={
            <>
              <Button type="button" variant="secondary" onClick={() => setUploadOpen(false)}>Cancel</Button>
              <Button type="button" variant="primary" onClick={submitUpload} loading={saving}>Upload</Button>
            </>
          }
        >
          <form className="doc-form" onSubmit={submitUpload}>
            <div className="doc-file-picker" onClick={() => fileRef.current?.click()}>
              <input ref={fileRef} type="file" hidden onChange={(e) => setFile(e.target.files[0] || null)} />
              <span className="doc-file-ico">📄</span>
              <span>{file ? `${file.name} · ${fmtBytes(file.size)}` : 'Choose a file (max 25 MB)'}</span>
            </div>
            {(allView || suspenseMode) && (
              <Select label="Client" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
                <option value="">— Suspense (file later) —</option>
                {clients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
              </Select>
            )}
            <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{groupedOptions}</Select>

            {/* Naming: system convention by default, custom name on request. */}
            <label className="doc-name-toggle">
              <input type="checkbox" checked={customName} onChange={(e) => setCustomName(e.target.checked)} />
              Use a custom name
            </label>
            {customName ? (
              <Input
                label="Custom name"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Enter document name"
              />
            ) : (
              <div className="doc-name-preview">
                <span className="doc-name-preview-label">Will be named</span>
                <span className="doc-name-preview-value">{namePreview || 'Pick a category (and client) to preview…'}</span>
              </div>
            )}

            <div className="doc-form-row">
              <Input label="Financial Year" value={form.financialYear} onChange={(e) => setForm({ ...form, financialYear: e.target.value })} placeholder="2025-26" />
              <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="final">Final</option>
                <option value="filed">Filed</option>
                <option value="archived">Archived</option>
              </Select>
            </div>
            {/* The date ON the document, not today's. Optional — left blank the
                document is simply filed without a month, which is right for the
                many that have no single meaningful date. */}
            <Input
              label="Document date (optional)"
              type="date"
              value={form.documentDate}
              onChange={(e) => setForm({ ...form, documentDate: e.target.value })}
              helperText="The date on the document itself — groups it into a month folder"
            />
            <Input label="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="kyc, verified" />
            {formError && <p className="doc-form-error">{formError}</p>}
          </form>
        </Modal>
      )}

      {/* Move / refile */}
      {moveDoc && (
        <Modal
          isOpen
          onClose={() => setMoveDoc(null)}
          title="File document"
          footer={
            <>
              <Button type="button" variant="secondary" onClick={() => setMoveDoc(null)}>Cancel</Button>
              <Button type="button" variant="primary" onClick={submitMove} loading={moving}>File here</Button>
            </>
          }
        >
          <form className="doc-form" onSubmit={(e) => { e.preventDefault(); submitMove(); }}>
            <p className="doc-move-name">📄 {moveDoc.title}</p>
            <Select label="Client" value={moveDoc.clientId} onChange={(e) => setMoveDoc({ ...moveDoc, clientId: e.target.value })}>
              <option value="">— Suspense (no client) —</option>
              {clients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
            </Select>
            <Select label="Folder (category)" value={moveDoc.category} onChange={(e) => setMoveDoc({ ...moveDoc, category: e.target.value })}>{groupedOptions}</Select>
            <Input label="Financial Year (optional)" value={moveDoc.financialYear} onChange={(e) => setMoveDoc({ ...moveDoc, financialYear: e.target.value })} placeholder="2025-26" />
          </form>
        </Modal>
      )}

      {/* Edit metadata */}
      {editDoc && (
        <Modal
          isOpen
          onClose={() => setEditDoc(null)}
          title="Edit document"
          footer={
            <>
              <Button type="button" variant="secondary" onClick={() => setEditDoc(null)}>Cancel</Button>
              <Button type="button" variant="primary" onClick={saveEdit}>Save</Button>
            </>
          }
        >
          <form className="doc-form" onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
            <Input label="Title" value={editDoc.title} onChange={(e) => setEditDoc({ ...editDoc, title: e.target.value })} />
            <Select label="Category" value={editDoc.category} onChange={(e) => setEditDoc({ ...editDoc, category: e.target.value })}>{groupedOptions}</Select>
            <div className="doc-form-row">
              <Input label="Financial Year" value={editDoc.financialYear} onChange={(e) => setEditDoc({ ...editDoc, financialYear: e.target.value })} placeholder="2025-26" />
              <Select label="Status" value={editDoc.status} onChange={(e) => setEditDoc({ ...editDoc, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="final">Final</option>
                <option value="filed">Filed</option>
                <option value="archived">Archived</option>
              </Select>
            </div>
            {/* Editable both ways: setting a date is how an already-filed
                document joins a month folder, and clearing it is how one filed
                under the wrong month gets out again. */}
            <Input
              label="Document date"
              type="date"
              value={editDoc.documentDate || ''}
              onChange={(e) => setEditDoc({ ...editDoc, documentDate: e.target.value })}
              helperText="Clear it to file this document as Undated"
            />
            <Input label="Tags" value={(editDoc.tags || []).join(', ')} onChange={(e) => setEditDoc({ ...editDoc, tags: e.target.value.split(',').map((s) => s.trim()) })} />
          </form>
        </Modal>
      )}

      {/* Preview */}
      {preview && (
        <Modal
          isOpen
          onClose={closePreview}
          title={preview.doc.title || preview.doc.fileName}
          size="lg"
          footer={
            <>
              <Button type="button" variant="secondary" onClick={() => download(preview.doc)}>Download</Button>
              <Button type="button" variant="primary" onClick={closePreview}>Close</Button>
            </>
          }
        >
          <div className="doc-preview">
            {preview.loading ? (
              <div className="doc-preview-msg">Loading preview…</div>
            ) : !preview.url ? (
              <div className="doc-preview-msg">Preview unavailable.</div>
            ) : preview.kind === 'image' ? (
              <img className="doc-preview-img" src={preview.url} alt={preview.doc.fileName} />
            ) : preview.kind === 'pdf' || preview.kind === 'text' ? (
              <iframe className="doc-preview-frame" src={preview.url} title={preview.doc.fileName} />
            ) : (
              <div className="doc-preview-msg">
                <p>No inline preview for this file type ({preview.doc.mimeType || 'unknown'}).</p>
                <Button type="button" variant="primary" onClick={() => download(preview.doc)}>Download to open</Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

export default DocumentsManager;
